// Intent: the Napper trick moved to money — after 1–3 months of entries the
// app PREDICTS the month: when income lands, when fixed costs hit, how much
// will be left ("kasa skończy się ok. 24." / "odłożysz ~800 zł"). All local
// arithmetic over the user's own entries — no AI, no guessing beyond averages.

import type { Ionicons } from '@expo/vector-icons';

import type { BudgetEntry, FixedCost, IncomeEntry, IncomeSource } from '@/types/domain';
import { lastMonths, monthKeyOf } from '@/store/income-store';

export interface MonthMarker {
  day: number;
  kind: 'INCOME' | 'COST';
  label: string;
  amountGr: number | null;
  /** Ionicons glyph shown inside the ring bubble (Napper-style band). */
  icon: keyof typeof Ionicons.glyphMap;
  /** Overdue and unanswered — the ring greys it out until the user decides. */
  pending?: boolean;
}

/**
 * An overdue, unanswered plan item ("pensja miała wpłynąć 10., nie wpłynęła").
 * Intent: the SINGLE source of truth for every "coś się zmieniło" question in
 * the app (Start card, Zarobki "do uzupełnienia", ring greying) — no screen
 * derives its own asking logic, so no event is ever asked about twice.
 */
export interface PendingItem {
  kind: 'INCOME' | 'COST';
  id: string;
  label: string;
  day: number;
  amountGr: number | null;
}

export interface MonthForecast {
  markers: MonthMarker[];
  /** Overdue items still counted in the forecast, awaiting the user's answer. */
  pending: PendingItem[];
  /** Expected total income this month (entered + still expected). */
  incomeGr: number;
  /** Forecast total outflow (spent so far + pace + remaining fixed costs). */
  outflowGr: number;
  /** incomeGr - outflowGr; null when there is no income data at all. */
  leftGr: number | null;
  /** Day of month the BUDGET runs out at current pace (null = doesn't). */
  runsOutDay: number | null;
}

/** Average of a source's last up-to-3 entries; fallback to expectedAmountGr. */
function typicalAmountGrOf(source: IncomeSource, entries: IncomeEntry[]): number {
  const own = entries
    .filter((e) => e.sourceId === source.id)
    .sort((a, b) => b.month.localeCompare(a.month))
    .slice(0, 3);
  if (own.length) return Math.round(own.reduce((s, e) => s + e.amountGr, 0) / own.length);
  return source.expectedAmountGr ?? 0;
}

export function monthForecastOf(
  sources: IncomeSource[],
  incomeEntries: IncomeEntry[],
  fixedCosts: FixedCost[],
  budgetEntries: BudgetEntry[],
  monthlyBudgetGr: number,
  now = new Date(),
): MonthForecast {
  const month = monthKeyOf(now);
  const today = now.getDate();
  const dim = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

  // "skipped" = the user explicitly answered "nie było w tym miesiącu" — the
  // item leaves the forecast AND the questions for this month only.
  const activeSources = sources.filter((s) => s.active && !(s.skippedMonths ?? []).includes(month));
  const activeCosts = fixedCosts.filter((c) => c.active && !(c.skippedMonths ?? []).includes(month));

  const enteredGr = incomeEntries.filter((e) => e.month === month).reduce((s, e) => s + e.amountGr, 0);
  const enteredIds = new Set(incomeEntries.filter((e) => e.month === month).map((e) => e.sourceId));

  // Ring markers: expected incomes + fixed costs, positioned by day. An overdue
  // unanswered item is flagged pending — drawn greyed until the user decides.
  const markers: MonthMarker[] = [
    ...activeSources
      .filter((s) => s.expectedDay !== null)
      .map((s) => ({
        day: s.expectedDay as number,
        kind: 'INCOME' as const,
        label: s.name,
        amountGr: typicalAmountGrOf(s, incomeEntries) || null,
        icon: 'cash-outline' as keyof typeof Ionicons.glyphMap,
        pending: (s.expectedDay as number) < today && !enteredIds.has(s.id),
      })),
    ...activeCosts.map((c) => ({
      day: c.dueDay ?? 1,
      kind: 'COST' as const,
      label: c.name,
      amountGr: c.amountGr,
      icon: 'receipt-outline' as keyof typeof Ionicons.glyphMap,
      pending: (c.dueDay ?? 1) < today && !c.paidMonths.includes(month),
    })),
  ].sort((a, b) => a.day - b.day);

  // Income: entered this month + typical amounts of sources still expected.
  // An overdue source (expectedDay passed, no entry) STAYS counted — the app
  // asks instead of silently assuming the salary arrived (Napper behaviour);
  // it leaves the forecast only via an entry or an explicit "nie było" skip.
  const expectedRestGr = activeSources
    .filter((s) => !enteredIds.has(s.id))
    .reduce((s, src) => s + typicalAmountGrOf(src, incomeEntries), 0);
  const incomeGr = enteredGr + expectedRestGr;

  // Outflow: spent so far + spending pace over remaining days + unpaid fixed
  // costs. Same rule: an unpaid cost past its due day STAYS counted until the
  // user answers "zapłacone" or "nie było" — never silently dropped.
  const monthEntries = budgetEntries.filter((e) => e.entryDate.startsWith(month));
  const spentGr = monthEntries.reduce((s, e) => s + e.amountGr, 0);
  const paceGr = today > 0 ? spentGr / today : 0;
  const unpaidCosts = activeCosts.filter((c) => !c.paidMonths.includes(month));
  const fixedRestGr = unpaidCosts.reduce((s, c) => s + c.amountGr, 0);
  const outflowGr = Math.round(spentGr + paceGr * (dim - today) + fixedRestGr);

  // Overdue & unanswered — the questions the Start card asks (see PendingItem).
  const pending: PendingItem[] = [
    ...activeSources
      .filter((s) => s.expectedDay !== null && s.expectedDay < today && !enteredIds.has(s.id))
      .map((s) => ({
        kind: 'INCOME' as const,
        id: s.id,
        label: s.name,
        day: s.expectedDay as number,
        amountGr: typicalAmountGrOf(s, incomeEntries) || null,
      })),
    ...unpaidCosts
      .filter((c) => (c.dueDay ?? 1) < today)
      .map((c) => ({
        kind: 'COST' as const,
        id: c.id,
        label: c.name,
        day: c.dueDay ?? 1,
        amountGr: c.amountGr,
      })),
  ].sort((a, b) => a.day - b.day);

  // "Kasa skończy się ok. X." — budget vs pace (only meaningful with a budget).
  let runsOutDay: number | null = null;
  if (monthlyBudgetGr > 0 && paceGr > 0) {
    const day = Math.floor(monthlyBudgetGr / paceGr);
    if (day < dim && day >= today) runsOutDay = day;
  }

  const hasAnyIncomeData = incomeEntries.length > 0 || activeSources.some((s) => s.expectedAmountGr);
  return {
    markers,
    pending,
    incomeGr,
    outflowGr,
    leftGr: hasAnyIncomeData ? incomeGr - outflowGr : null,
    runsOutDay,
  };
}

// --- „Siła prognozy" 0–100 — grows with data completeness (Napper score) ---

export interface ForecastStrength {
  score: number;
  /** 1 = Uczenie się, 2 = Kalibrowanie, 3 = Dopracowywanie */
  stage: 1 | 2 | 3;
  monthsOfData: number;
}

export function forecastStrengthOf(
  sources: IncomeSource[],
  incomeEntries: IncomeEntry[],
  fixedCosts: FixedCost[],
  budgetEntries: BudgetEntry[],
  goalsCount: number,
  now = new Date(),
): ForecastStrength {
  // Months (of the last 12) that have at least one income entry.
  const window = lastMonths(12, now);
  const monthsOfData = window.filter((m) => incomeEntries.some((e) => e.month === m)).length;

  // 40 pts: income history depth (6+ months = full)
  const incomePts = Math.min(6, monthsOfData) * (40 / 6);
  // 25 pts: fixed costs mapped (3+ = full)
  const costPts = Math.min(3, fixedCosts.filter((c) => c.active).length) * (25 / 3);
  // 20 pts: budget activity this month (8+ entries = full)
  const month = monthKeyOf(now);
  const budgetPts = Math.min(8, budgetEntries.filter((e) => e.entryDate.startsWith(month)).length) * (20 / 8);
  // 15 pts: sources with an expected day (the app can actually predict timing)
  const active = sources.filter((s) => s.active);
  const dayPts = active.length ? (active.filter((s) => s.expectedDay !== null).length / active.length) * 15 : 0;

  const score = Math.round(incomePts + costPts + budgetPts + dayPts);
  const stage: 1 | 2 | 3 = monthsOfData <= 1 ? 1 : monthsOfData <= 3 ? 2 : 3;
  return { score: Math.min(100, score), stage, monthsOfData };
}
